// Bidirectional symbol↔panel-row hover for EquationBlock (PRD 07 §8.9).
//
// Listens with delegated mouseover/mouseout (and focusin/focusout for
// keyboard parity) on the equation container. A hovered element is a
// symbol if it carries `id=<symbol_id>` (inline math span emitted by
// KaTeX) OR `data-symbol-id=<symbol_id>` (panel row). The hook returns
// the matched symbol_id, or null when no symbol is under the pointer.
//
// Why a hook instead of inlining in EquationBlock: the widget renders
// equations through a separate path and needs the same matching logic
// without React state. Sharing the matcher (matchSymbolFromEvent) keeps
// both consumers honest about which DOM signals count as "hovering a
// symbol."

import { useEffect, useState } from "react";

export function matchSymbolFromEvent(
  target: EventTarget | null,
  container: HTMLElement,
  validSymbolIds: ReadonlySet<string>,
): string | null {
  if (!(target instanceof Element)) return null;
  if (!container.contains(target)) return null;
  // Panel rows carry data-symbol-id; inline math carries id (KaTeX
  // emits one node per `\htmlId{<sid>}{…}`). Look for either.
  const hit = target.closest<HTMLElement>("[data-symbol-id], [id]");
  if (!hit || !container.contains(hit)) return null;
  const sid = hit.getAttribute("data-symbol-id") ?? hit.id;
  if (!sid || !validSymbolIds.has(sid)) return null;
  return sid;
}

export function useEquationHover(
  containerRef: React.RefObject<HTMLElement | null>,
  symbolIds: ReadonlyArray<string>,
): string | null {
  const [hovered, setHovered] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const valid = new Set(symbolIds);

    const onEnter = (e: Event) => {
      const sid = matchSymbolFromEvent(e.target, container, valid);
      if (sid) setHovered(sid);
    };
    const onLeave = (e: Event) => {
      // Guard against mouseout fired when the cursor crosses from a
      // child to its parent inside the same hit — relatedTarget still
      // inside the container means we're still hovering something.
      const me = e as MouseEvent;
      const rel = me.relatedTarget;
      if (rel instanceof Node && container.contains(rel)) {
        const sid = matchSymbolFromEvent(rel, container, valid);
        setHovered(sid);
        return;
      }
      setHovered(null);
    };

    container.addEventListener("mouseover", onEnter);
    container.addEventListener("mouseout", onLeave);
    container.addEventListener("focusin", onEnter);
    container.addEventListener("focusout", onLeave);
    return () => {
      container.removeEventListener("mouseover", onEnter);
      container.removeEventListener("mouseout", onLeave);
      container.removeEventListener("focusin", onEnter);
      container.removeEventListener("focusout", onLeave);
    };
  }, [containerRef, symbolIds]);

  return hovered;
}
